import {
  faBookMedical,
  faBookOpen,
  faBullseye,
  faCopy,
  faExclamationTriangle,
  faEye,
  faEyeSlash,
  faGripVertical,
  faShareSquare,
  faToolbox
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, ButtonGroup, ButtonToolbar } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import { injectIntl } from "react-intl";
import { TEMPLATE_CREATOR, VISIBLE_KEY } from "../../../common/constant";
import Visibility from "../../../component/common/VisibleContainer";
import { isQuestionKeyDuplicated } from "../../../ducks/surveyEditor";
import { cleanText, QuestionIcon } from "../../../utils/QuestionUtils";
import Circle from "../../common/Circle";
import IconTooltip from "../../common/IconTooltip";
import TrashButton from "../../common/TrashButton";
import QuestionTriggerPopup from "./QuestionTriggerPopup";

const SAP_PROJECT = "projectData";

/**
 * Represents question placed (dragged) in the main container in the survey editor.
 * Questions can be reordered, meaning question is both a drag (the one moved) and a drop (to which question's position
 * it is moved) target.
 */
class Question extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false
    };
  }

  handleExpand() {
    const { question, questions, questionNumber } = this.props;

    // TODO Refactorings, understand what this is actually doing...???
    for (let i = 0; i < questions.length; i++) {
      // Prevent SAP_PROJECT configuration
      if (questions[i].type === SAP_PROJECT) {
        continue;
      } else if (questions[i].name === question.name) {
        this.props.questionExpanded === questionNumber
          ? this.props.onQuestionExpand(-1)
          : this.props.onQuestionExpand(questionNumber);
      } else {
        if (this.props.questionExpanded === questionNumber) {
          this.props.onQuestionExpand(-1);
        }
      }
    }
  }

  handleRemoveQuestion(event) {
    // do not bubble to parent
    event.stopPropagation();
    const { pageName, removeQuestion, question } = this.props;

    this.props.showDialog(
      null,
      () => {
        removeQuestion(pageName, question.name);
      },
      "survey.editor.confirm.question.deletion",
      "survey.editor.confirm.question.deletion",
      "confirmDialog.confirmLabel",
      "confirmDialog.dismissLabel"
    );
  }

  handleRemoveLibraryQuestion(event) {
    // do not bubble to parent
    event.stopPropagation();
    const { pageName, removeQuestion, question } = this.props;

    this.props.showDialog(
      null,
      () => {
        removeQuestion(pageName, question.name);
      },
      "survey.editor.confirm.question.deletion",
      "survey.editor.confirm.question.deletion.library",
      "confirmDialog.confirmLabel",
      "confirmDialog.dismissLabel"
    );
  }

  handleQuestionCopy(event) {
    event.stopPropagation();
    const { showModalForSavingQuestionToLibrary, pageName, copyQuestion, question, questionSync } = this.props;
    copyQuestion(pageName, question);
    if (questionSync) {
      showModalForSavingQuestionToLibrary(question);
    }
  }

  onConfirmRemove(pageName, question) {
    const { removeQuestion } = this.props;
    removeQuestion(pageName, question.name);
  }

  onSaveQuestionToLibrary(question, questionSync, event) {
    event.stopPropagation();
    const { showModalForSavingQuestionToLibrary, showModalForSavingQuestionToMyLibrary } = this.props;

    questionSync
      ? showModalForSavingQuestionToLibrary(question, questionSync)
      : showModalForSavingQuestionToMyLibrary(question);
  }

  handleVisibilityChange(event) {
    event.stopPropagation();
    const { pageName, question, changeQuestionField } = this.props;

    changeQuestionField(pageName, question.name, [VISIBLE_KEY], !question.visible);
  }

  handleOpenTriggerPopup(event) {
    const { filterInvisible } = this.props;
    event.stopPropagation();

    filterInvisible();
    this.setState({ showModal: true });
  }

  handleCloseTriggerPopup() {
    this.setState({ showModal: false });
  }

  handleDragStart(question, event) {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "copyMove";
    const dragData = JSON.stringify({
      type: "reorderQuestion",
      questionName: question.name
    });

    event.dataTransfer.setData("text", dragData);
  }

  handleDrop(question, event) {
    const dragData = JSON.parse(event.dataTransfer.getData("text"));
    const { pageName, reorderQuestion } = this.props;

    if (dragData.type === "reorderQuestion") {
      reorderQuestion(pageName, dragData.questionName, question.name);
      event.stopPropagation();
      event.preventDefault();
    }
  }

  shouldRenderTrigger() {
    const { question, questionSync } = this.props;
    return (
      !questionSync &&
      question.choices &&
      question.choices.length > 0 &&
      question.type !== "matrixSingleChoice" &&
      question.type !== "matrixMultipleChoice"
    );
  }

  render() {
    const {
      component,
      changeQuestionField,
      pageName,
      question,
      questionNumber,
      addQuestionChoice,
      removeQuestionChoice,
      intl,
      reorderChoice,
      reorderPage,
      setValidator,
      setValidatorProperty,
      editSubchoice,
      confirmSubchoice,
      cancelSubchoice,
      loadPredefinedList,
      loadIndicators,
      addIndicatorToQuestion,
      removeIndicatorFromQuestion,
      allIndicators,
      questionLibrary,
      questionSync,
      isAdminUser,
      editorPages
    } = this.props;
    const indicatorLibraryQuestion =
      questionLibrary && question.libraryQuestionId
        ? questionLibrary.find((q) => q.id === question.libraryQuestionId)
        : undefined;
    const myLibraryQuestion = !indicatorLibraryQuestion && question.libraryQuestionId;
    // If we have projectData type of question we set name to it
    const questionTypeProjectData =
      question.type === "projectData" ? `${intl.formatMessage({ id: "survey.editor.toolbox.projectData" })}` : null;
    const title = question.title ? cleanText(question.title) : question.name;
    const questionTitle = questionTypeProjectData ? questionTypeProjectData : title;

    const questionKeyDuplicated = isQuestionKeyDuplicated({pages: editorPages}, question.key);
    return (
      <Accordion.Item eventKey={questionNumber.toString()} className="bg-light">
        <Accordion.Header
          as={Card.Header}
          onDrop={this.handleDrop.bind(this, question)}
          onClick={this.handleExpand.bind(this)}
          className="btn-light btn pb-2 w-100"
        >
          <ButtonToolbar className="gap-lg align-items-center flex-nowrap w-100">
          <Button
              variant="link"
              title={intl.formatMessage({ id: "survey.editor.tooltip.question.move" })}
              draggable
              onDragStart={this.handleDragStart.bind(this, question)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{ cursor: "grab" }}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </Button>
            <Circle>{questionNumber}</Circle>
            <QuestionIcon type={question.type} />
            <span className="text-truncate" title={questionTitle}>
              {questionTitle}
            </span>
            {myLibraryQuestion && (
              <IconTooltip 
                iconClass={"text-primary"} 
                iconDefinition={faBookOpen} 
                tooltipMessageId="survey.editor.tooltip.question.myLibraryQuestion"
              />
            )}

            {indicatorLibraryQuestion && (
              <IconTooltip 
                iconClass={"text-primary"} 
                iconDefinition={faToolbox} 
                tooltipMessageId="survey.editor.tooltip.question.libraryQuestion"
              />
            )}

            {question.hasIndicators && (
              <IconTooltip 
                iconClass={"text-danger"} 
                iconDefinition={faBullseye} 
                tooltipMessageId="survey.editor.tooltip.question.library.indicator"
              />
            )}

            {questionKeyDuplicated && (
              <IconTooltip 
                iconClass={"text-danger"} 
                iconDefinition={faExclamationTriangle} 
                tooltipMessageId="survey.version.compare.warning.duplicate.keys"
              />
            )}
              
            <ButtonGroup className="ms-auto me-2">
              {!questionSync && (
                <Visibility roles={[TEMPLATE_CREATOR]}>
                  <Button
                    variant="link"
                    onClick={this.onSaveQuestionToLibrary.bind(this, question, questionSync)}
                    title={intl.formatMessage({ id: "survey.editor.tooltip.question.library" })}
                  >
                    <FontAwesomeIcon icon={faBookMedical} />
                  </Button>
                </Visibility>
              )}
              {question.type !== "projectData" && (
                <Button
                  variant="link"
                  onClick={this.handleQuestionCopy.bind(this)}
                  title={intl.formatMessage({ id: "survey.editor.tooltip.question.copy" })}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
              )}
              {this.shouldRenderTrigger(question) && (
                <Button
                  variant="link"
                  onClick={this.handleOpenTriggerPopup.bind(this)}
                  title={intl.formatMessage({ id: "survey.editor.tooltip.question.trigger" })}
                >
                  <FontAwesomeIcon icon={faShareSquare} />
                </Button>
              )}
              {!questionSync ? (
                <>
                  <Button
                    variant="link"
                    onClick={this.handleVisibilityChange.bind(this)}
                    title={intl.formatMessage({ id: "survey.editor.tooltip.question.visibility" })}
                  >
                    <FontAwesomeIcon icon={question.visible ? faEye : faEyeSlash} />
                  </Button>
                  <TrashButton
                    onClick={this.handleRemoveQuestion.bind(this)}
                    titleId="survey.editor.tooltip.question.delete"
                  />
                </>
              ) : (
                <TrashButton
                  onClick={this.handleRemoveLibraryQuestion.bind(this)}
                  titleId="survey.editor.tooltip.question.delete"
                />
              )}
            </ButtonGroup>
          </ButtonToolbar>
        </Accordion.Header>
        <Accordion.Body>
        {component ? (
              React.cloneElement(component, {
                changeQuestionField,
                pageName,
                question,
                addQuestionChoice,
                removeQuestionChoice,
                reorderChoice,
                reorderPage,
                setValidator,
                setValidatorProperty,
                editSubchoice,
                confirmSubchoice,
                cancelSubchoice,
                loadPredefinedList,
                intl,
                loadIndicators,
                addIndicatorToQuestion,
                removeIndicatorFromQuestion,
                allIndicators,
                libraryQuestion: indicatorLibraryQuestion,
                isAdminUser
              })
            ) : (
              <h5>N/A</h5>
            )}
            {this.shouldRenderTrigger(question) && (
              <QuestionTriggerPopup
                {...this.props}
                show={this.state.showModal}
                closeModal={this.handleCloseTriggerPopup.bind(this)}
              />
            )}
        </Accordion.Body>
      </Accordion.Item>
    );
  }
}

Question.propTypes = {
  allIndicators: PropTypes.array,
  removeIndicatorFromQuestion: PropTypes.func,
  removeQuestionChoice: PropTypes.func,
  removeQuestion: PropTypes.func,
  addIndicatorToQuestion: PropTypes.func,
  addQuestionChoice: PropTypes.func,
  loadIndicators: PropTypes.func,
  loadPredefinedList: PropTypes.func.isRequired,
  confirmSubchoice: PropTypes.func.isRequired,
  cancelSubchoice: PropTypes.func.isRequired,
  editSubchoice: PropTypes.func.isRequired,
  setValidatorProperty: PropTypes.func,
  changeQuestionField: PropTypes.func,
  setValidator: PropTypes.func,
  reorderChoice: PropTypes.func.isRequired,
  reorderPage: PropTypes.func.isRequired,
  intl: PropTypes.object,
  question: PropTypes.object,
  component: PropTypes.object,
  questions: PropTypes.array,
  questionNumber: PropTypes.number,
  pageName: PropTypes.string,
  reorderQuestion: PropTypes.func.isRequired,
  showDialog: PropTypes.func,
  filterInvisible: PropTypes.func,
  copyQuestion: PropTypes.func,
  showModalForSavingQuestionToLibrary: PropTypes.func,
  questionLibrary: PropTypes.array,
  questionSync: PropTypes.bool
};

export default injectIntl(Question);
